import React, { useState, useEffect } from "react";
import { Edit2 } from "lucide-react";
import { collection, query, where, getDocs, updateDoc, doc } from "firebase/firestore";
import { db } from "../firebase/config";

const statusOptions = [
  "à contacter",
  "closé",
  "à rappeler",
  "pas intéressé",
  "rdv booké",
  "follow-up",
  "no show",
];

const LeadInfoModal = ({ isOpen, onClose, leadData, onUpdateStatus, onDeleteLead }) => {
  const [currentStatus, setCurrentStatus] = useState("");
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [comment, setComment] = useState("");
  const [salesUsers, setSalesUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [isAssigningTask, setIsAssigningTask] = useState(false);
  const [assignees, setAssignees] = useState([]);

  useEffect(() => {
    if (leadData) {
      setCurrentStatus(leadData.status || "");
      setComment(leadData.comment || "");
      setAssignees(leadData.assignees || []);
    }
  }, [leadData]);

  useEffect(() => {
    const fetchSalesUsers = async () => {
      try {
        const q = query(collection(db, "users"), where("role", "==", "sales"));
        const querySnapshot = await getDocs(q);
        const users = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSalesUsers(users);
      } catch (error) {
        console.error("Error fetching sales users:", error);
      }
    };

    if (isAssigningTask) {
      fetchSalesUsers();
    }
  }, [isAssigningTask]);

  if (!isOpen || !leadData) {
    return null;
  }

  const getStatusBadge = (status) => {
    const map = {
      "à contacter": { color: "text-green-500", bg: "bg-green-500/10" },
      closé: { color: "text-green-600", bg: "bg-green-600/10" },
      "à rappeler": { color: "text-orange-500", bg: "bg-orange-500/10" },
      "pas intéressé": { color: "text-red-500", bg: "bg-red-500/10" },
      "rdv booké": { color: "text-blue-500", bg: "bg-blue-500/10" },
      "follow-up": { color: "text-purple-500", bg: "bg-purple-500/10" },
      "no show": { color: "text-red-400", bg: "bg-red-400/10" },
    };
    const badge = map[status] || {
      color: "text-gray-400",
      bg: "bg-gray-600/10",
    };
    return (
      <span
        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.color}`}
      >
        ● {status}
      </span>
    );
  };

  const handleStatusChange = (e) => {
    setCurrentStatus(e.target.value);
    setIsEditingStatus(false);
    onUpdateStatus(leadData.id, e.target.value);
  };

  const handleValidate = async () => {
    try {
      const taskRef = doc(db, "kanban_tasks", leadData.id);
      await updateDoc(taskRef, {
        assignees: assignees,
        comment: comment,
      });
      console.log("Lead updated successfully with assignees and comment!");
      onClose();
    } catch (error) {
      console.error("Error updating lead:", error);
    }
  };

  const handleAssignTask = () => {
    setIsAssigningTask(true);
  };

  const handleAddAssignee = () => {
    if (selectedUser && !assignees.includes(selectedUser)) {
      setAssignees([...assignees, selectedUser]);
      setSelectedUser("");
    }
  };

  const handleRemoveAssignee = (userId) => {
    setAssignees(assignees.filter((id) => id !== userId));
  };

  const handleDelete = () => {
    if (window.confirm("Are you sure you want to delete this lead?")) {
      console.log("Supprimer clicked for lead:", leadData.id);
      onDeleteLead(leadData.id);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
      <div className="bg-[#19223c] rounded-lg shadow-xl w-full max-w-md md:max-w-xl lg:max-w-2xl text-white relative p-10 py-5 h-[700px] scrollbar-thin overflow-y-auto">
        {/* Header */}
        <div className="flex relative justify-between items-center p-4 border-b border-gray-700">
          <div className="flex items-center justify-between mt-3 gap-2 text-gray-400 text-sm w-full">
            <div>
              <span>{leadData.date.split(" ")[0]}</span> -{" "}
              <span>{leadData.date.split(" ")[1].substring(0, 5)}</span>
            </div>
            <span className="ml-4 px-2 py-1 bg-blue-600 rounded-full text-xs text-white">
              {leadData.source}
            </span>
          </div>
          <button
            onClick={() => {
              setIsAssigningTask(false);
              onClose();
            }}
            className="absolute -right-6 -top-2 text-gray-400 hover:text-white text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* User Info */}
        <div className="p-4 border-b flex justify-between items-center border-gray-700">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-lg font-medium text-white">
              {leadData.avatar}
            </div>
            <div className="text-start">
              <div className="font-medium text-white text-lg">{leadData.name}</div>
              <div className="text-sm text-gray-400">{leadData.email}</div>
              <div className="text-sm text-gray-400">{leadData.phone}</div>
            </div>
          </div>
          <button className="bg-[#154245] text-green-500 px-4 py-2 rounded text-sm font-medium">
            À CONTACTER
          </button>
        </div>

        {/* Parcours */}
        <div className="p-4 border-b border-gray-700">
          <h3 className="text-start text-gray-300 font-bold text-sm mb-2">Parcours</h3>
          <p className="text-gray-400 text-sm text-start">
            Lorem ipsum dolor sit amet, consectetur adipiscing, sed do eiusmod tempor incididunt
          </p>
        </div>

        {/* Budget */}
        <div className="p-4 border-b border-gray-700">
          <h3 className="text-gray-300 text-sm mb-2 font-bold text-start">Budget</h3>
          <p className="text-gray-400 text-sm text-start">
            {leadData.budget || "Lorem ipsum dolor sit amet, consectetur adipiscing, sed do eiusmod tempor incididunt..."}
          </p>
        </div>

        {/* Status */}
        <div className="p-4 border-b border-gray-700 flex justify-between items-center">
          <div>
            <h3 className="text-gray-300 text-sm mb-2 font-bold text-start">Status</h3>
            {isEditingStatus ? (
              <select
                autoFocus
                value={currentStatus}
                onChange={handleStatusChange}
                onBlur={() => setIsEditingStatus(false)}
                className="bg-[#0b1739] text-white border border-slate-600 px-3 py-1 rounded text-sm uppercase"
              >
                {statusOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            ) : (
              <div
                className="cursor-pointer uppercase"
                onClick={() => setIsEditingStatus(true)}
              >
                {getStatusBadge(currentStatus)}
              </div>
            )}
          </div>
          <button
            className="bg-white text-[#0071E3] flex items-center gap-2 px-4 py-2 rounded text-sm font-medium transition-colors"
            onClick={() => setIsEditingStatus(true)}
          >
            <Edit2 className="w-4 h-4" /> Modifier
          </button>
        </div>

        {/* Assign Task Section */}
        {isAssigningTask && (
          <div className="p-4 border-b border-gray-700">
            <h3 className="text-gray-300 text-sm mb-2 font-bold text-start">Assigner à un utilisateur</h3>
            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              className="w-full bg-[#0b1739] border border-slate-600 rounded p-3 text-sm text-gray-300 focus:outline-none focus:border-blue-500"
            >
              <option value="">Sélectionner un utilisateur</option>
              {salesUsers.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name || user.email}
                </option>
              ))}
            </select>
            <div className="mt-3 flex justify-end gap-2">
              <button
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                onClick={() => setIsAssigningTask(false)}
              >
                Annuler
              </button>
              <button
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                onClick={handleAddAssignee}
                disabled={!selectedUser}
              >
                Add
              </button>
            </div>
            {/* Display Assignees */}
            {assignees.length > 0 && (
              <div className="mt-4">
                <h4 className="text-gray-300 text-sm mb-2 font-bold text-start">Assignees</h4>
                <div className="flex flex-wrap gap-2">
                  {assignees.map((userId) => {
                    const user = salesUsers.find((u) => u.id === userId);
                    return (
                      <div
                        key={userId}
                        className="flex items-center gap-2 px-3 py-1 bg-blue-600 text-white rounded-full text-sm"
                      >
                        <span>{user ? user.name || user.email : userId}</span>
                        <button
                          onClick={() => handleRemoveAssignee(userId)}
                          className="text-red-400 hover:text-red-500"
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Comment Section */}
        <div className="p-4 border-b border-gray-700">
          <h3 className="text-gray-300 text-sm mb-2 font-bold text-start">Mettre un commentaire</h3>
          <textarea
            className="w-full bg-[#0b1739] border border-slate-600 rounded p-3 text-sm text-gray-300 focus:outline-none focus:border-blue-500"
            rows="4"
            placeholder="Lorem ipsum dolor sit amet, consectetur adipiscing, sed do eiusmod tempor incididunt..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          ></textarea>
        </div>

        {/* Action Buttons */}
        <div className="p-4 flex justify-between gap-3">
          <button
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors"
            onClick={handleValidate}
          >
            Valider
          </button>
          <button
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors"
            onClick={handleAssignTask}
          >
            Assigner la tâche
          </button>
          <button
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors"
            onClick={handleDelete}
          >
            Supprimer
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeadInfoModal;